import React from 'react';
import { 
    Paper, 
    Typography, 
    Button, 
    Box,
    CircularProgress,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

interface RedemptionStatusProps {
    isValid: boolean;
    isRedeemed: boolean;
    redeemedAt?: string;
    onRedeem: () => Promise<void>;
    errorMessage?: string;
}

const RedemptionStatus: React.FC<RedemptionStatusProps> = ({
    isValid,
    isRedeemed,
    redeemedAt,
    onRedeem,
    errorMessage
}) => {
    const theme = useTheme();
    const [isRedeeming, setIsRedeeming] = React.useState(false);

    const handleRedeem = async () => {
        setIsRedeeming(true);
        try {
            await onRedeem();
        } finally {
            setIsRedeeming(false);
        }
    };

    const getStatusColor = () => {
        if (errorMessage) return theme.palette.error.main;
        if (isRedeemed) return theme.palette.success.main;
        if (!isValid) return theme.palette.warning.main;
        return theme.palette.primary.main;
    };

    const getStatusIcon = () => {
        if (errorMessage) return <ErrorIcon fontSize="large" color="error" />;
        if (isRedeemed) return <CheckCircleIcon fontSize="large" color="success" />;
        if (!isValid) return <WarningIcon fontSize="large" color="warning" />;
        return null;
    };

    const getStatusMessage = () => {
        if (errorMessage) return errorMessage;
        if (isRedeemed) {
            return redeemedAt 
                ? `Redeemed on ${new Date(redeemedAt).toLocaleDateString()} at ${new Date(redeemedAt).toLocaleTimeString()}`
                : 'Coupon has been redeemed';
        }
        if (!isValid) return 'This coupon is not valid';
        return 'Ready to redeem';
    };

    return (
        <Paper 
            elevation={3}
            sx={{ 
                p: 3,
                backgroundColor: alpha(getStatusColor(), 0.1),
                mb: 4
            }}
        >
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                {getStatusIcon()}
                
                <Typography 
                    variant="h5" 
                    component="h2" 
                    align="center"
                    color={getStatusColor()}
                    gutterBottom
                >
                    {getStatusMessage()}
                </Typography>

                {!errorMessage && !isRedeemed && isValid && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRedeem}
                        disabled={isRedeeming}
                        sx={{ mt: 2 }}
                    >
                        {isRedeeming ? (
                            <>
                                <CircularProgress 
                                    size={20} 
                                    color="inherit" 
                                    sx={{ mr: 1 }} 
                                />
                                Redeeming...
                            </>
                        ) : (
                            'Redeem Now'
                        )}
                    </Button>
                )}
            </Box>
        </Paper>
    );
};

export default RedemptionStatus;
